/** Remove capability fragments before error reporting or analytics initialize. */
import {
  definePersistedKey,
  readPersistedValue,
  writePersistedValue,
} from "@/utils/persistedStorage";

const TOKEN = /^[a-f\d]{24}\.[A-Za-z\d_-]{43}$/i;

export function customerPortalPath(baseUrl = import.meta.env.BASE_URL) {
  return `${baseUrl.replace(/\/?$/, "/")}service-portal`;
}
const portalAccess = definePersistedKey<string, "session">({
  owner: "dispatch",
  name: "customer-access",
  scope: "session",
  storage: "session",
  version: 1,
  parse: (raw) => (TOKEN.test(raw) ? raw : null),
  serialize: (value) => value,
  legacyKeys: () => ["dispatch-customer-access"],
});

export function resolveCustomerPortalAccess(
  url: URL,
  savedToken: string | null,
  baseUrl = import.meta.env.BASE_URL,
) {
  if (url.pathname !== customerPortalPath(baseUrl))
    return { token: null, removeFragment: false };
  const fragment = new URLSearchParams(url.hash.slice(1));
  const supplied = fragment.get("access");
  const candidate = supplied && TOKEN.test(supplied) ? supplied : savedToken;
  return {
    token: candidate && TOKEN.test(candidate) ? candidate : null,
    removeFragment: supplied !== null,
  };
}

let token: string | null = null;
const listeners = new Set<() => void>();

function captureCustomerPortalAccess() {
  const saved = readPersistedValue(portalAccess) ?? token;
  const access = resolveCustomerPortalAccess(
    new URL(window.location.href),
    saved,
  );
  token = access.token;
  if (access.removeFragment) {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
    // The page retains its in-memory capability when private browsing denies storage.
    if (token) writePersistedValue(portalAccess, token);
  }
  for (const listener of listeners) listener();
  return access.removeFragment;
}

// This bootstrap module also loads in the Node unit-test lane.
if ("window" in globalThis) {
  captureCustomerPortalAccess();
  window.addEventListener(
    "hashchange",
    (event) => {
      if (captureCustomerPortalAccess()) {
        // HashChangeEvent.newURL includes the secret even after replaceState.
        // Prevent later navigation/analytics listeners from retaining that URL.
        event.stopImmediatePropagation();
      }
    },
    { capture: true },
  );
}

export const getCustomerPortalToken = () => token;
export function subscribeCustomerPortalAccess(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
